import React, { useMemo } from "react"
import { useTheme } from '~/contexts/ThemeContext'
import { PdpProvider } from '~/contexts/PdpContext'
// import { ProductProvider } from '~/contexts/ProductContext'
import { graphql } from "gatsby"
import PdpInit from '~/initializers/pdp-init'
import { useLocation } from '@reach/router'
import { imgCDN, imgSize } from '~/utils/helpers'

import Pdp from '~/templates/pdp'
import { Seo } from '~/components/shared/seo'

// import TestControl from '~/components/pdp/testControl'
// import TestNew from '~/components/pdp/testNew'


export default function ProductTemplate({ data }) {
  const { setupProduct, setupVariants } = PdpInit()

  const productData = useMemo(() => {
    return setupProduct(data.product)
  }, [])

  const variantData = useMemo(() => {
    return setupVariants({variants: data.product.variants, isGift: data.product.is_gift})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if ( data.product.pl_pima === 99998 ) {
    return (<div />)
  }

  return (
    <PdpProvider productData={productData} variantData={variantData}>
      <Pdp />
    </PdpProvider>
  );
}

// <ProductProvider productData={data.product}>
//   <Pdp />
// </ProductProvider>

export const Head = ({ data: { product } }) => {
  const { isMobile } = useTheme()
  const location = useLocation();

  const fabric_tag = (product.filters && product.filters.fabric && product.filters.fabric[0]) || null
  let material = fabric_tag ? fabric_tag.replace('filter-fabric:','') : ''
  if (material.toLowerCase().includes('slub') || material.toLowerCase().includes('pima')) {
    material = material + ' Cotton'
  }

  let desc = ''
  if (product.handle.includes('gift')) desc = 'Buck Mason Gift Card'
  if (!product.handle.includes('gift') && product.short_description) {
    desc = product.short_description.replace(/<[^>]*(>|$)|&nbsp;|&zwnj;|&raquo;|&laquo;|&gt;/g, '')
  }

  const variants = product.variants || []
  let sku = ''
  let avail = true

  let image = product.image
  if (!image && product.media.length > 0) {
    image = product.media.find(x => x.type === 'IMAGE') ? product.media.find(x => x.type === 'IMAGE').src : null
  }
  const images = product.media.filter(x => x.type === 'IMAGE' && x.src !== image).map(x => x.src)
  image = image ? imgCDN(image) : ''

  if (variants.length > 0) {
    let variant = variants.find(x => x.available) ? variants.find(x => x.available) : variants[0]
    sku = variant.sku 
    avail = variant.available
    if (location.search.includes('variant')) {
      const url = new URL(location.href);
      const variantId = url.searchParams.get('variant');
      const url_variant = product.variants.find(x => x.id === parseInt(variantId))
      if (url_variant) {
        sku = url_variant.sku
        avail = url_variant.available
      }
    }
  }

  const color = product.color ? `${product.color.group} ${product.color.name}` : ''

  let reviews = ''
  if (product.okendo_count > 0) {
    reviews = `,"aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "${product.okendo_avg}",
      "ratingCount": "${product.okendo_count}"
    }`
  }

  return (
    <>
      <Seo 
        title={product.title}
        description={product.vdescription ? product.vdescription : `${product.title} - ${product.product_line.short_description}`}
        image={image}
        url={`/products/${product.handle}`}
      >
        <script id="prod_struc_data" type="application/ld+json">
          {`{
            "@context": "http://schema.org/",
            "@type": "Product",
            "name": "${product.title}",
            "url": "${location.href}",
            "image": "${image}",
            "color": "${color}",
            "material": "${material}",
            "description": "${desc}",
            "mpn": "${product.id}",
            "sku": "${sku}",
            "brand": {
              "@type": "Brand",
              "name": "Buck Mason"
            },
            "offers": {
              "@type": "Offer",
              "priceCurrency": "USD",
              "price": "${product.first_product_price}",
              "priceValidUntil": "${new Date().getFullYear() + 1}-12-31",
              "availability": "${avail ? 'InStock' : 'OutOfStock'}",
              "seller": {
                "@type": "Organization",
                "name": "Buck Mason"
              }
            }
            ${reviews}
          }`}
        </script>
        <meta property="product:price:amount" content={product.first_product_price} />
        <meta property="product:price:currency" content="USD" />
        <meta property="og:brand" content="Buck Mason" />
        <meta property="og:price:amount" content={product.first_product_price} />
        <meta property="og:price:currency" content="USD" />
      </Seo>
      {image && (
        <link rel="preload" href={imgSize(image,isMobile ? 900 : 1200)} as="image" type="image/jpeg" />
      )}
      {isMobile && images?.length > 0 && images.map(x => 
        <link key={x} rel="preload" href={imgSize(image,900)} as="image" type="image/jpeg" />
      )}
      {images?.length > 0 && images.map(x => 
        <link key={x} rel="preload" href={imgSize(image,300)} as="image" type="image/jpeg" />
      )}
    </>
  )
}


export const pageQuery = graphql`
  query ($handle: String!) {
    product(handle: { eq: $handle }) {
      mid
      alt_cuts
      available
      broken
      category
      callouts {
        mid
        data
      }
      color
      filters
      fit_size
      first_full_price
      first_product_price
      handle
      hem
      is_alt_cut
      is_vintage
      is_preorder
      image
      low
      media
      modelinfo
      okendo_count
      okendo_avg
      percent_off
      pl_pima
      product_line {
        mid
        gender
        filters
        category
        colors
        fit_size
        handle
        name
        short_description
        description
        sizing
        sztyp
        has_reviews
        blocks {
          type
          settings
        }
      }
      shop_id
      short_description
      sku
      sizing
      tags
      title
      variants

      recommends {
        mid 
        handle
        title
        product_line {
          name
        }
        color
        category
        image
        first_product_price
        first_full_price
        shop_id
        variants
      }

      outfits {
        mid
        shop_id
        handle
        category
        image
        color
        title
        first_full_price
        first_product_price
        product_line {
          name
          colors
        }
      }
    }
  }
`

// removed outfills as they aren't being used
// outfills {
//   mid
//   shop_id
//   handle
//   category
//   image
//   color
//   title
//   first_full_price
//   first_product_price
//   product_line {
//     name
//     colors
//   }
// }




